import { router, usePage } from "@inertiajs/react"
import { Clock, Trash2 } from "lucide-react"

import { useSwipeGesture } from "@/hooks/use-swipe-gesture"
import { flushListCache, flushListsIndexCache } from "@/lib/cache"
import { getTimeRemaining, getUrgencyLevel } from "@/lib/urgency"
import { cn } from "@/lib/utils"
import { listTaskCompletionPath, listTaskPath } from "@/routes"
import type { AggregateTask, SharedData } from "@/types"

interface DashboardTaskItemProps {
  task: AggregateTask
  isSelected?: boolean
  onClick?: () => void
  onDelete?: () => void
}

export function DashboardTaskItem({
  task,
  isSelected = false,
  onClick,
  onDelete,
}: DashboardTaskItemProps) {
  const { today } = usePage<SharedData>().props
  const urgencyLevel = getUrgencyLevel(task, task.list.auto_archive_days)
  const isDyingToday = task.death_date === today

  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation()
    const flushCache = () => {
      flushListCache(task.list.id)
      flushListsIndexCache()
    }

    if (task.status === "completed") {
      router.delete(listTaskCompletionPath(task.list.id, task.id), {
        preserveScroll: true,
        onSuccess: flushCache,
      })
    } else {
      router.post(
        listTaskCompletionPath(task.list.id, task.id),
        {},
        {
          preserveScroll: true,
          onSuccess: flushCache,
        },
      )
    }
  }

  const handleDelete = () => {
    router.delete(listTaskPath(task.list.id, task.id), {
      preserveScroll: true,
      preserveUrl: true,
      onSuccess: () => {
        flushListCache(task.list.id)
        flushListsIndexCache()
        onDelete?.()
      },
    })
  }

  const { swipeState, swipeHandlers, fallbackTouchHandlers, getSwipeStyle } =
    useSwipeGesture({
      enabled: task.status === "active",
      canSwipeLeft: task.status === "active",
      canSwipeRight: false,
      onSwipeLeft: handleDelete,
    })

  const { offset: swipeOffset, direction: swipeDirection } = swipeState

  return (
    <div className="relative overflow-hidden rounded-lg">
      {/* Delete action background */}
      {swipeDirection === "left" && task.status === "active" && (
        <div
          className="absolute inset-y-0 right-0 flex items-center justify-center bg-red-500/90 px-6"
          style={{ width: Math.max(Math.abs(swipeOffset), 80) }}
        >
          <Trash2 className="h-5 w-5 text-white" style={{ minWidth: "20px" }} />
        </div>
      )}

      <div
        {...swipeHandlers}
        {...fallbackTouchHandlers}
        style={getSwipeStyle()}
        onClick={onClick}
        className={cn(
          "group border-border bg-bg-card hover:border-bone-dim flex cursor-pointer items-center gap-4 rounded-lg border px-4 py-3 transition-all",
          isSelected &&
            "ring-red-bright bg-red-dim/30 border-red-bright/50 ring-2",
          isDyingToday &&
            "animate-dying-heartbeat border-l-red-bright from-red-dim to-bg-card border-l-[3px] bg-gradient-to-r",
          !isDyingToday &&
            urgencyLevel === 2 &&
            "border-l-red-bright from-red-dim to-bg-card border-l-[3px] bg-gradient-to-r",
          urgencyLevel === 1 &&
            "border-l-yellow from-yellow-dim to-bg-card border-l-[3px] bg-gradient-to-r",
          urgencyLevel === 3 &&
            "border-l-bone-dim to-bg-card border-l-[3px] bg-gradient-to-r from-[rgba(74,72,68,0.15)] opacity-70",
        )}
      >
        <button
          onClick={handleToggleComplete}
          className={cn(
            "flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[5px] border-2 transition-all",
            "border-bone-dim bg-bg-input hover:border-bone-muted",
            urgencyLevel === 3 && "opacity-60",
          )}
        />

        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "mb-1 text-[0.95rem] leading-snug font-medium",
              urgencyLevel < 3 && "text-bone",
              urgencyLevel === 3 && "text-bone-muted",
            )}
          >
            {task.description}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="border-border bg-bg-hover text-bone-muted rounded border px-2 py-0.5 font-mono text-[0.65rem] tracking-wider uppercase">
              {task.list.name}
            </span>
            {(() => {
              const timeInfo = getTimeRemaining(task.death_date, today)
              return timeInfo.text ? (
                <span
                  className={cn(
                    "flex items-center gap-1 font-mono font-medium tracking-wide",
                    timeInfo.urgencyLevel === "critical" && "text-red-bright",
                    timeInfo.urgencyLevel === "warning" && "text-yellow",
                    timeInfo.urgencyLevel === "safe" && "text-bone-muted",
                  )}
                >
                  <Clock className="h-3 w-3" />
                  {timeInfo.text}
                </span>
              ) : null
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}
